import { useRoleLabels } from "../lib/RoleLabelsContext";
import { exerciseNow, setExerciseTimeout, clearExerciseTimeout } from "../exercises/pause-clock";
import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { RecommendationPlanFields, emptyRecommendationPlan, recommendationPlanInput } from "./RecommendationPlanFields";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useEffect, useMemo, useRef, useState } from "react";
import type { EinmaleinsExerciseConfig, EinmaleinsReihe, EinmaleinsRunSettings } from "@calega/content";
import type { EinmaleinsRun } from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { api } from "../lib/api";
import { startAdaptiveAssignmentRun } from "../exercises/learning-api";
import { useRealtime } from "../realtime/RealtimeContext";
import { abortExerciseRun, submitExerciseAnswer, submitSkippedExerciseAnswer } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";
import { ExerciseDurationSelect } from "./ExerciseSettings";
import { ExerciseMenu } from "./ExerciseMenu";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";

type Phase = "running" | "feedback" | "complete";
interface EinmaleinsAnswer { itemId: string; value: number; expected: number; correct: boolean }
interface State {
  result?: { score: number; maxScore: number };
  runId: string;
  run: EinmaleinsRun;
  index: number;
  phase: Phase;
  value: string;
  correctionCount: number;
  intermediateValues: number[];
  answers: EinmaleinsAnswer[];
  feedback: EinmaleinsAnswer | null;
  endsAt: number | null;
}

const rows: EinmaleinsReihe[] = [2, 3, 4, 5, 6, 7, 8, 9, 10];

export function Einmaleins({ account, config, pageId, adaptiveAssignmentId }: { account: Account; config: EinmaleinsExerciseConfig; pageId: string; adaptiveAssignmentId?: string }) {
  const labels = useRoleLabels();
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const inSyncSession = Boolean(realtime.session);
  const follower = inSyncSession && !facilitator;
  const canControl = !inSyncSession || facilitator;
  const canAnswer = !inSyncSession || facilitator || follower;
  const [trainingMode, setTrainingMode] = useState<EinmaleinsRunSettings["trainingMode"]>(config.defaultTrainingMode);
  const [mode, setMode] = useState<EinmaleinsRunSettings["mode"]>(config.defaultMode);
  const [count, setCount] = useState<EinmaleinsRunSettings["count"]>(config.defaultCount);
  const [durationSeconds, setDurationSeconds] = useState<EinmaleinsRunSettings["durationSeconds"]>(config.defaultDurationSeconds);
  const [selectedRows, setSelectedRows] = useState<EinmaleinsReihe[]>(config.defaultRows);
  const hasSelection = trainingMode === "core" || selectedRows.length > 0;
  const canCreateAdaptive = !adaptiveAssignmentId
    && Boolean(account.modules?.adaptiveAssignments)
    && account.roles.some((role) => role === "ORGANIZATION" || role === "THERAPIST");
  const [adaptiveComposerOpen, setAdaptiveComposerOpen] = useState(false);
  const [adaptiveLearners, setAdaptiveLearners] = useState<Array<{ id: string; displayName: string }>>([]);
  const [adaptiveLearnersLoaded, setAdaptiveLearnersLoaded] = useState(false);
  const [adaptiveLearnerId, setAdaptiveLearnerId] = useState(account.actingAs?.id ?? "");
  const [adaptiveTitle, setAdaptiveTitle] = useState("1×1-Training für zuhause");
  const [adaptiveMessage, setAdaptiveMessage] = useState("");
  const [recommendationPlan, setRecommendationPlan] = useState(emptyRecommendationPlan);
  const [adaptiveProfile, setAdaptiveProfile] = useState<"gentle" | "balanced" | "focused">("balanced");
  const [exercise, setExercise] = useState<State | null>(null);
  useLearningDisplayItem(exercise?.runId, exercise?.index ?? 0, Boolean(exercise && exercise.phase !== "complete"));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [, tick] = useState(0);
  const answerRef = useRef<HTMLInputElement>(null);
  const correctCount = useMemo(() => exercise?.answers.filter(({ correct }) => correct).length ?? 0, [exercise?.answers]);

  function publish(state: State | null) {
    realtime.publishExercise({ exerciseType: "einmaleins", pageId, state });
  }

  useEffect(() => {
    type SyncedState = Omit<State, "value" | "correctionCount" | "intermediateValues"> & Partial<Pick<State, "value" | "correctionCount" | "intermediateValues">>;
    const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; state?: SyncedState | null } | null;
    if (synced?.exerciseType === "einmaleins" && synced.pageId === pageId) {
      setExercise(synced.state ? { ...synced.state, value: synced.state.value ?? "", correctionCount: synced.state.correctionCount ?? 0, intermediateValues: synced.state.intermediateValues ?? [] } : null);
    }
  }, [realtime.state.exercise, pageId]);

  useEffect(() => {
    if (exercise?.phase === "running") answerRef.current?.focus();
  }, [exercise?.phase, exercise?.index]);

  useEffect(() => {
    if (!exercise?.endsAt || exercise.phase === "complete") return;
    const interval = window.setInterval(() => tick((current) => current + 1), 250);
    return () => window.clearInterval(interval);
  }, [exercise?.endsAt, exercise?.phase]);

  useEffect(() => {
    if (busy || !exercise?.endsAt || exercise.phase === "complete" || !canControl) return;
    const timeout = setExerciseTimeout(() => finish().catch((error) => setMessage(error.message)), Math.max(0, exercise.endsAt - exerciseNow(realtime.serverNow)) + 20);
    return () => clearExerciseTimeout(timeout);
  }, [exercise?.endsAt, exercise?.phase, exercise?.index, canControl, busy]);

  useEffect(() => {
    if (exercise?.phase !== "feedback" || !canControl || inSyncSession) return;
    const timeout = setExerciseTimeout(() => advance().catch((error) => setMessage(error.message)), exercise.feedback?.correct ? 650 : 1_250);
    return () => clearExerciseTimeout(timeout);
  }, [exercise?.phase, exercise?.index, exercise?.feedback?.correct, canControl, inSyncSession, busy]);

  function settings(): EinmaleinsRunSettings {
    return { trainingMode, mode, count, durationSeconds, rows: trainingMode === "core" ? [] : selectedRows };
  }

  useEffect(() => {
    if (!canCreateAdaptive) return;
    let mounted = true;
    setAdaptiveLearnersLoaded(false);
    api<Array<{ id: string; displayName: string }>>("/auth/acting-as/options")
      .then((learners) => {
        if (!mounted) return;
        setAdaptiveLearners(learners);
        setAdaptiveLearnerId((current) => {
          if (learners.some(({ id }) => id === current)) return current;
          const preferred = account.actingAs?.id ?? "";
          return learners.some(({ id }) => id === preferred) ? preferred : "";
        });
        setAdaptiveLearnersLoaded(true);
      })
      .catch(() => { if (mounted) { setAdaptiveLearners([]); setAdaptiveLearnersLoaded(true); } });
    return () => { mounted = false; };
  }, [account.actingAs?.id, canCreateAdaptive]);

  async function createAdaptiveAssignment() {
    if (!canCreateAdaptive || !adaptiveLearnerId || !adaptiveTitle.trim() || !hasSelection || busy) return;
    setBusy(true);
    try {
      await api("/learning/adaptive-assignments", {
        method: "POST",
        body: JSON.stringify({
          learnerId: adaptiveLearnerId,
          contentPageId: pageId,
          title: adaptiveTitle,
          message: adaptiveMessage,
          ...recommendationPlanInput(recommendationPlan),
          exerciseType: "einmaleins",
          config: { ...settings(), focusProfile: adaptiveProfile },
        }),
      });
      setAdaptiveComposerOpen(false);
      setAdaptiveMessage("");
      setRecommendationPlan(emptyRecommendationPlan);
      setMessage("Die adaptive Empfehlung wurde angelegt.");
    } finally { setBusy(false); }
  }

  async function start(runSettings = settings()) {
    if (busy || !canControl || (!adaptiveAssignmentId && runSettings.trainingMode !== "core" && !runSettings.rows.length)) return;
    setBusy(true);
    try {
      const response = adaptiveAssignmentId
        ? await startAdaptiveAssignmentRun<{ runId: string; exercise: EinmaleinsRun }>(adaptiveAssignmentId)
        : await startExerciseRun<{ runId: string; exercise: EinmaleinsRun }>({ contentPageId: pageId, settings: runSettings });
      const next: State = {
        runId: response.runId,
        run: response.exercise,
        index: 0,
        phase: "running",
        value: "",
        correctionCount: 0,
        intermediateValues: [],
        answers: [],
        feedback: null,
        endsAt: response.exercise.settings.mode === "time" ? exerciseNow(realtime.serverNow) + response.exercise.settings.durationSeconds * 1_000 : null,
      };
      setExercise(next);
      setMessage("");
      publish(next);
    } finally { setBusy(false); }
  }

  async function abort() {
    if (exercise && exercise.phase !== "complete") await abortExerciseRun(exercise.runId);
  }

  async function back() {
    if (!exercise || busy || !canControl) return;
    setBusy(true);
    try {
      await abort();
      setMode(exercise.run.settings.mode);
      setCount(exercise.run.settings.count);
      setDurationSeconds(exercise.run.settings.durationSeconds);
      setTrainingMode(exercise.run.settings.trainingMode ?? "rows");
      if (exercise.run.settings.trainingMode !== "core") setSelectedRows(exercise.run.settings.rows);
      setExercise(null);
      publish(null);
    } finally { setBusy(false); }
  }

  async function remix() {
    if (!exercise || busy || !canControl) return;
    const runSettings = exercise.run.settings;
    setBusy(true);
    try { await abort(); } finally { setBusy(false); }
    await start(runSettings);
  }

  function updateValue(value: string) {
    if (!exercise || exercise.phase !== "running" || busy || !canAnswer) return;
    const previous = exercise.value.trim();
    const previousNumber = Number(previous);
    const corrected = previous !== "" && Number.isSafeInteger(previousNumber) && value !== exercise.value && value.trim().length <= previous.length;
    const next = {
      ...exercise,
      value,
      correctionCount: exercise.correctionCount + (corrected ? 1 : 0),
      intermediateValues: corrected ? [...exercise.intermediateValues, previousNumber].slice(-100) : exercise.intermediateValues,
    };
    setExercise(next);
    publish(next);
  }

  async function check() {
    if (!exercise || exercise.phase !== "running" || busy || !canAnswer) return;
    const item = exercise.run.items[exercise.index];
    const parsed = Number(exercise.value);
    if (!item || exercise.value.trim() === "" || !Number.isSafeInteger(parsed)) return;
    setBusy(true);
    try {
      await submitExerciseAnswer(exercise.runId, { type: "einmaleins", itemId: item.id, value: parsed, skipped: false, correctionCount: exercise.correctionCount, intermediateValues: exercise.intermediateValues });
      const feedback: EinmaleinsAnswer = { itemId: item.id, value: parsed, expected: item.result, correct: parsed === item.result };
      const next = { ...exercise, phase: "feedback" as const, answers: [...exercise.answers, feedback], feedback };
      setExercise(next);
      publish(next);
    } finally { setBusy(false); }
  }

  async function skip() {
    if (!exercise || exercise.phase !== "running" || busy || !canControl) return;
    const item = exercise.run.items[exercise.index];
    if (!item) return;
    setBusy(true);
    try { await submitSkippedExerciseAnswer(exercise.runId, item.id, "einmaleins"); } finally { setBusy(false); }
    await advance();
  }

  async function advance() {
    if (!exercise || busy || !canControl || exercise.phase === "complete") return;
    if ((exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length) return finish();
    const next = { ...exercise, index: exercise.index + 1, phase: "running" as const, value: "", correctionCount: 0, intermediateValues: [], feedback: null };
    setExercise(next);
    publish(next);
  }

  async function finish() {
    if (!exercise || exercise.phase === "complete" || busy || !canControl) return;
    await completion.complete(exercise.runId, (result) => {
      const next = { ...exercise, result, phase: "complete" as const };
      setExercise(next);
      publish(next);
      setMessage("");
    });
  }

  async function closeCompletedRun(repeat: boolean) {
    if (!exercise || exercise.phase !== "complete") return;
    if (repeat) await remix();
    else await back();
  }

  function toggleRow(row: EinmaleinsReihe) {
    setSelectedRows((current) => current.includes(row)
      ? current.filter((item) => item !== row)
      : [...current, row].sort((left, right) => left - right));
  }

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (follower && !exercise) return <ExerciseWaitingCard className="mathe-exercise einmaleins-exercise" />;

  if (!exercise) return <section className="mathe-exercise einmaleins-exercise">
    <div className="einmaleins-intro"><div><p className="quiz-instructions learning-text">{config.instructions}</p>{!adaptiveAssignmentId && <p className="muted">Wähle einzelne Reihen oder trainiere die Kernaufgaben gemischt.</p>}</div><img src="/brand/lepi-mathe.webp" alt="" /></div>
    {adaptiveAssignmentId && <div className="alert info"><strong>Adaptives 1×1-Training</strong><p>Dein Auftrag legt die Aufgaben fest. Unsichere und fällige Aufgaben werden im nächsten Durchgang bevorzugt ausgewählt.</p></div>}
    {!adaptiveAssignmentId && <div className="mathe-settings einmaleins-settings">
      <fieldset><legend>Was möchtest du trainieren?</legend><div className="blitz-choice-row"><label className="checkbox-label"><input type="radio" name={`${pageId}-einmaleins-training-mode`} checked={trainingMode === "rows"} onChange={() => setTrainingMode("rows")} />Reihen üben</label><label className="checkbox-label"><input type="radio" name={`${pageId}-einmaleins-training-mode`} checked={trainingMode === "core"} onChange={() => setTrainingMode("core")} />Kernaufgaben</label></div></fieldset>
      {trainingMode === "core" ? <fieldset><legend>Kernaufgaben</legend><p>1er-, 2er-, 5er- und 10er-Reihe sowie Quadrataufgaben von 1 × 1 bis 10 × 10 werden gemischt.</p></fieldset> : <fieldset><legend>Welche Reihen möchtest du trainieren?</legend><div className="einmaleins-row-grid">{rows.map((row) => <label className="einmaleins-row-option" key={row}><input type="checkbox" checked={selectedRows.includes(row)} onChange={() => toggleRow(row)} /><span>{row}er</span></label>)}</div></fieldset>}
      <fieldset><legend>Durchlauf</legend><div className="mathe-run-settings"><div className="blitz-choice-row"><label className="checkbox-label"><input type="radio" name={`${pageId}-einmaleins-mode`} checked={mode === "count"} onChange={() => setMode("count")} />Feste Anzahl</label><label className="checkbox-label"><input type="radio" name={`${pageId}-einmaleins-mode`} checked={mode === "time"} onChange={() => setMode("time")} />Auf Zeit</label></div>{mode === "count" ? <label>Anzahl Aufgaben<select value={count} onChange={(event) => setCount(Number(event.target.value) as EinmaleinsRunSettings["count"])}>{[5, 10, 15, 20].map((item) => <option key={item}>{item}</option>)}</select></label> : <ExerciseDurationSelect value={durationSeconds} onChange={(value) => setDurationSeconds(value as EinmaleinsRunSettings["durationSeconds"])} />}</div></fieldset>
    </div>}
    <button className="primary" disabled={busy || !canControl || (!adaptiveAssignmentId && !hasSelection)} onClick={() => start().catch((error) => setMessage(error.message))}>{adaptiveAssignmentId ? "Nächsten Durchgang starten" : "1×1-Training starten"}</button>
    {!adaptiveAssignmentId && canCreateAdaptive && <>
      <button className="secondary adaptive-create-trigger" disabled={busy || !hasSelection || !adaptiveLearnersLoaded || adaptiveLearners.length === 0} onClick={() => setAdaptiveComposerOpen(true)}>Adaptive Empfehlung anlegen</button>
      {adaptiveLearnersLoaded && adaptiveLearners.length === 0 && <p className="muted">{labels.LEARNER.plural}: Keine freigegebenen Konten verfügbar.</p>}
      {adaptiveComposerOpen && <section className="adaptive-assignment-composer" role="dialog" aria-modal="true" aria-labelledby={`${pageId}-adaptive-title`}>
        <div className="adaptive-assignment-composer-heading"><div><p className="eyebrow">Adaptive Empfehlung</p><h2 id={`${pageId}-adaptive-title`}>Empfehlung für zuhause anlegen</h2></div><button type="button" className="secondary small" onClick={() => setAdaptiveComposerOpen(false)}>Schließen</button></div>
        <p className="muted">Die aktuell gewählten Einstellungen werden für jeden neuen Durchgang adaptiv neu zusammengestellt.</p>
        <div className="adaptive-settings-summary"><strong>Gewählte Einstellungen</strong><span>{trainingMode === "core" ? "Kernaufgaben" : selectedRows.map((row) => `${row}er`).join(", ")} · {mode === "count" ? `${count} Aufgaben` : `${durationSeconds / 60} Minuten`}</span></div>
        <label>{labels.LEARNER.singular}<select value={adaptiveLearnerId} onChange={(event) => setAdaptiveLearnerId(event.target.value)}><option value="">{labels.LEARNER.singular} auswählen</option>{adaptiveLearners.map((learner) => <option value={learner.id} key={learner.id}>{learner.displayName}</option>)}</select></label>
        <label>Titel<input value={adaptiveTitle} onChange={(event) => setAdaptiveTitle(event.target.value)} maxLength={160} /></label>
        <label>Anweisung<textarea value={adaptiveMessage} onChange={(event) => setAdaptiveMessage(event.target.value)} maxLength={1_000} placeholder={`Optionale Hinweise (${labels.LEARNER.singular})`} /></label>
        <RecommendationPlanFields value={recommendationPlan} onChange={setRecommendationPlan} />
        <label>Adaptive Gewichtung<select value={adaptiveProfile} onChange={(event) => setAdaptiveProfile(event.target.value as typeof adaptiveProfile)}><option value="gentle">Sanft</option><option value="balanced">Ausgewogen</option><option value="focused">Fokussiert</option></select></label>
        <div className="adaptive-assignment-composer-actions"><button type="button" className="secondary" onClick={() => setAdaptiveComposerOpen(false)}>Abbrechen</button><button type="button" className="primary" disabled={busy || !adaptiveLearnerId || !adaptiveTitle.trim()} onClick={() => createAdaptiveAssignment().catch((error) => setMessage(error.message))}>Adaptive Empfehlung speichern</button></div>
      </section>}
    </>}
    {!adaptiveAssignmentId && !hasSelection && <div className="alert info">Wähle mindestens eine 1×1-Reihe aus.</div>}
    {message && <div className="alert info">{message}</div>}
  </section>;

  const item = exercise.run.items[exercise.index];
  const remaining = exercise.endsAt ? Math.max(0, Math.ceil((exercise.endsAt - exerciseNow(realtime.serverNow)) / 1_000)) : null;
  const last = (exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length;
  return <section className="mathe-exercise einmaleins-exercise">
    {canControl && exercise.phase !== "complete" && <ExerciseMenu><button className="secondary small" disabled={busy} onClick={() => back().catch((error) => setMessage(error.message))}>← Zurück zu den Einstellungen</button><button className="secondary small" disabled={busy} onClick={() => remix().catch((error) => setMessage(error.message))}>↻ Neu mischen</button></ExerciseMenu>}
    {exercise.run.settings.trainingMode === "core" && <p className="muted">Kernaufgaben</p>}
    <div className="exercise-progress">{exercise.run.settings.mode === "time" ? `${exercise.answers.length} Aufgaben beantwortet · ${remaining ?? 0} s` : `Aufgabe ${Math.min(exercise.index + 1, exercise.run.items.length)} von ${exercise.run.items.length}`}</div>
    {exercise.phase === "complete" ? <><div className="reime-result"><strong>{exercise.result?.score ?? correctCount} von {exercise.result?.maxScore ?? exercise.answers.length} Aufgaben richtig</strong><p>Der Durchlauf ist abgeschlossen.</p></div>{canControl && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}{exercise.phase === "complete" && <CompletedRunHistory key={exercise.runId} runId={exercise.runId} />}</> : item && <>
      <div className="mathe-card einmaleins-card"><div className="mathe-calculation" aria-label={`${item.left} mal ${item.right}`}><span>{item.left}</span><span className="operator">×</span><span>{item.right}</span><span>=</span><input ref={answerRef} className={exercise.phase === "feedback" ? exercise.feedback?.correct ? "correct" : "wrong" : ""} type="text" inputMode="numeric" value={exercise.phase === "feedback" && exercise.feedback ? String(exercise.feedback.value) : exercise.value} disabled={exercise.phase !== "running" || busy || !canAnswer} onChange={(event) => updateValue(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") check().catch((error) => setMessage(error.message)); }} aria-label="Ergebnis" placeholder="?" /></div></div>
      {exercise.phase === "feedback" && <div className={`answer-feedback ${exercise.feedback?.correct ? "correct" : "wrong"}`}>{exercise.feedback?.correct ? "Richtig gerechnet!" : <>Nicht ganz. Richtig ist <strong>{exercise.feedback?.expected ?? ""}</strong>.</>}</div>}
      {exercise.phase === "running" && <div className="mathe-actions"><button className="primary" disabled={busy || !canAnswer || exercise.value.trim() === ""} onClick={() => check().catch((error) => setMessage(error.message))}>Überprüfen</button>{canControl && <button className="secondary" disabled={busy} onClick={() => skip().catch((error) => setMessage(error.message))}>Überspringen</button>}</div>}
      {exercise.phase === "feedback" && inSyncSession && canControl && <div className="mathe-actions"><button className="primary" disabled={busy} onClick={() => advance().catch((error) => setMessage(error.message))}>{last ? "Auswertung ansehen" : "Nächste Aufgabe"}</button></div>}
    </>}
    {message && <div className="alert info">{message}</div>}
  </section>;
}
